/**
 * The FAVORITOS page grid.
 *
 * Same approach as the recently-viewed carousel: the ids are local, the
 * markup comes from this section through the Section Rendering API on the
 * /search route, which is the only route that gives a section a populated
 * `search` object.
 */
class MunneFavoritesGrid extends HTMLElement {
  /* The cards: the list's own items. Not just [data-product-id], which the
     heart inside every card carries too; matching it is what used to move
     the hearts out of their cards when reordering. */
  static CARD = '[data-favorites-list] > [data-product-id]';

  connectedCallback() {
    this.empty = this.querySelector('[data-favorites-empty]');
    this.heading = this.closest('.munne-favorites-page')?.querySelector('.munne-favorites-page__heading');
    this.results = this.querySelector('[data-favorites-results]');
    if (!this.results) return;

    this.onChange = this.onChange.bind(this);
    document.addEventListener(window.MunneFavorites.CHANGE_EVENT, this.onChange);

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(window.MunneFavorites.CHANGE_EVENT, this.onChange);
  }

  /** A heart was ticked or unticked on this very page. An unticked one just
   *  drops its card. A ticked one, from a row below the grid such as the
   *  best sellers under the empty state, has no card yet: that needs the
   *  markup again. */
  onChange(event) {
    const ids = event.detail.ids;
    const cards = Array.from(this.results.querySelectorAll(MunneFavoritesGrid.CARD));

    cards.forEach((card) => {
      if (ids.indexOf(card.dataset.productId) === -1) card.remove();
    });

    const shown = cards.map((card) => card.dataset.productId);
    if (ids.some((id) => shown.indexOf(id) === -1)) {
      this.render();
      return;
    }

    this.toggleEmpty(this.results.querySelector(MunneFavoritesGrid.CARD) === null);
  }

  /* Empty is the line and, below the section, the rows set to show only
     then. The page title stays for screen readers but leaves the screen:
     the mockup has the line alone. */
  toggleEmpty(isEmpty) {
    if (this.empty) this.empty.hidden = !isEmpty;
    this.results.hidden = isEmpty;
    this.heading?.classList.toggle('visually-hidden', isEmpty);
    document.querySelectorAll('[data-munne-no-favorites]').forEach((row) => {
      row.hidden = !isEmpty;
    });
  }

  async render() {
    // Several hearts in a row start several fetches; only the latest lands.
    const request = (this.request = (this.request || 0) + 1);
    const ids = window.MunneFavorites.read().slice(0, parseInt(this.dataset.limit, 10) || 50);

    if (ids.length === 0) {
      this.toggleEmpty(true);
      return;
    }

    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const url = `${this.dataset.searchUrl}?section_id=${encodeURIComponent(this.dataset.sectionId)}&type=product&q=${encodeURIComponent(query)}`;

    let markup = null;
    try {
      const response = await fetch(url);
      if (response.ok) markup = await response.text();
    } catch (error) {
      // Offline or blocked: shown as empty below.
    }

    // A newer render started meanwhile; its answer is the one that counts,
    // failed or not.
    if (request !== this.request) return;
    if (markup === null) return this.toggleEmpty(true);

    const results = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-favorites-results]');

    if (!results || results.querySelector(MunneFavoritesGrid.CARD) === null) {
      // Every favourite has since been deleted or unpublished.
      return this.toggleEmpty(true);
    }

    this.reorder(results, ids);
    this.results.innerHTML = results.innerHTML;
    this.toggleEmpty(false);
  }

  /** Search answers in its own order; put the cards back in most-recent-first. */
  reorder(results, ids) {
    const list = results.querySelector('[data-favorites-list]');
    if (!list) return;

    Array.from(list.querySelectorAll(':scope > [data-product-id]'))
      .sort((a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId))
      .forEach((card) => list.appendChild(card));
  }
}

if (!customElements.get('munne-favorites-grid')) {
  customElements.define('munne-favorites-grid', MunneFavoritesGrid);
}
